import React, { useCallback, useState, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import styled from 'styled-components';

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0f1a 0%, #1a1f2e 50%, #0d1421 100%);
`;

const Header = styled.div`
  padding: 20px 30px;
  background: rgba(30, 40, 60, 0.8);
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  span.badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
  }
`;

const FlowContainer = styled.div`
  flex: 1;
  position: relative;
`;

const Sidebar = styled.div`
  position: absolute;
  left: 20px;
  top: 20px;
  background: rgba(30, 40, 60, 0.95);
  border: 1px solid rgba(100, 150, 200, 0.3);
  border-radius: 12px;
  padding: 16px;
  z-index: 10;
  width: 200px;
  backdrop-filter: blur(10px);
`;

const SidebarTitle = styled.h3`
  color: #fff;
  font-size: 14px;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
`;

const DragNode = styled.div`
  background: ${props => props.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  padding: 10px 14px;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  margin-bottom: 10px;
  cursor: grab;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const NodeIcon = styled.span`
  font-size: 16px;
`;

// ============================================
// CUSTOM NODE COMPONENTS
// ============================================

const BaseNode = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  min-width: 180px;
  color: #fff;
  font-size: 13px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  border: 2px solid transparent;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const NodeLabel = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NodeSubtext = styled.div`
  font-size: 11px;
  opacity: 0.8;
`;

const QuestionNode = ({ data }) => (
  <BaseNode style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
    <Handle type="target" position={Position.Top} style={{ background: '#fff' }} />
    <NodeLabel>❓ {data.label}</NodeLabel>
    <NodeSubtext>{data.subtext || 'Ask the user a question'}</NodeSubtext>
    <Handle type="source" position={Position.Bottom} id="yes" style={{ left: '30%', background: '#4ade80' }} />
    <Handle type="source" position={Position.Bottom} id="no" style={{ left: '70%', background: '#f87171' }} />
  </BaseNode>
);

const ConditionNode = ({ data }) => (
  <BaseNode style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
    <Handle type="target" position={Position.Top} style={{ background: '#fff' }} />
    <NodeLabel>⚡ {data.label}</NodeLabel>
    <NodeSubtext>{data.subtext || 'Check a condition'}</NodeSubtext>
    <Handle type="source" position={Position.Bottom} id="true" style={{ left: '30%', background: '#4ade80' }} />
    <Handle type="source" position={Position.Bottom} id="false" style={{ left: '70%', background: '#f87171' }} />
  </BaseNode>
);

const DocumentNode = ({ data }) => (
  <BaseNode style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
    <Handle type="target" position={Position.Top} style={{ background: '#fff' }} />
    <NodeLabel>📄 {data.label}</NodeLabel>
    <NodeSubtext>{data.subtext || 'Request a document'}</NodeSubtext>
    <Handle type="source" position={Position.Bottom} style={{ background: '#fff' }} />
  </BaseNode>
);

const AIAnalysisNode = ({ data }) => (
  <BaseNode style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
    <Handle type="target" position={Position.Top} style={{ background: '#fff' }} />
    <NodeLabel>🤖 {data.label}</NodeLabel>
    <NodeSubtext>{data.subtext || 'AI analyzes response/document'}</NodeSubtext>
    <Handle type="source" position={Position.Bottom} id="pass" style={{ left: '30%', background: '#4ade80' }} />
    <Handle type="source" position={Position.Bottom} id="fail" style={{ left: '70%', background: '#f87171' }} />
  </BaseNode>
);

const OutcomeNode = ({ data }) => (
  <BaseNode style={{ background: data.success ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
    <Handle type="target" position={Position.Top} style={{ background: '#fff' }} />
    <NodeLabel>{data.success ? '✅' : '❌'} {data.label}</NodeLabel>
    <NodeSubtext>{data.subtext || (data.success ? 'Successful outcome' : 'Negative outcome')}</NodeSubtext>
  </BaseNode>
);

const StartNode = ({ data }) => (
  <BaseNode style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
    <NodeLabel>🚀 {data.label}</NodeLabel>
    <NodeSubtext>Flow starts here</NodeSubtext>
    <Handle type="source" position={Position.Bottom} style={{ background: '#fff' }} />
  </BaseNode>
);

// ============================================
// INITIAL DATA - Immigration Work Permit Example
// ============================================

const initialNodes = [
  {
    id: 'start',
    type: 'start',
    position: { x: 250, y: 0 },
    data: { label: 'Work Permit Request' },
  },
  {
    id: 'q1',
    type: 'question',
    position: { x: 250, y: 100 },
    data: { label: 'Worked in Canada?', subtext: 'Have you worked in Canada before?' },
  },
  {
    id: 'q2',
    type: 'question',
    position: { x: 450, y: 220 },
    data: { label: 'What was your job?', subtext: 'What type of work did you do?' },
  },
  {
    id: 'other-path',
    type: 'outcome',
    position: { x: 50, y: 220 },
    data: { label: 'Education Path', subtext: 'Check education credentials', success: true },
  },
  {
    id: 'cond1',
    type: 'condition',
    position: { x: 450, y: 340 },
    data: { label: 'Job = Waiter/Officiant?', subtext: 'Problematic for work permit' },
  },
  {
    id: 'doc1',
    type: 'document',
    position: { x: 550, y: 460 },
    data: { label: 'Request Salary Page', subtext: 'Get proof of employment' },
  },
  {
    id: 'good-job',
    type: 'outcome',
    position: { x: 300, y: 460 },
    data: { label: 'Eligible Job', subtext: 'Proceed with application', success: true },
  },
  {
    id: 'ai1',
    type: 'aiAnalysis',
    position: { x: 550, y: 580 },
    data: { label: 'Analyze Document', subtext: 'Check if "officiant" is mentioned' },
  },
  {
    id: 'success',
    type: 'outcome',
    position: { x: 450, y: 720 },
    data: { label: 'Claim Kitchen Worker', subtext: 'Good for work permit!', success: true },
  },
  {
    id: 'fail',
    type: 'outcome',
    position: { x: 700, y: 720 },
    data: { label: 'Ineligible Position', subtext: 'Cannot proceed', success: false },
  },
];

const initialEdges = [
  { id: 'e1', source: 'start', target: 'q1', animated: true, style: { stroke: '#667eea' } },
  { id: 'e2', source: 'q1', sourceHandle: 'yes', target: 'q2', label: 'Yes', style: { stroke: '#4ade80' } },
  { id: 'e3', source: 'q1', sourceHandle: 'no', target: 'other-path', label: 'No', style: { stroke: '#f87171' } },
  { id: 'e4', source: 'q2', target: 'cond1', animated: true, style: { stroke: '#667eea' } },
  { id: 'e5', source: 'cond1', sourceHandle: 'true', target: 'doc1', label: 'Yes', style: { stroke: '#f87171' } },
  { id: 'e6', source: 'cond1', sourceHandle: 'false', target: 'good-job', label: 'No', style: { stroke: '#4ade80' } },
  { id: 'e7', source: 'doc1', target: 'ai1', animated: true, style: { stroke: '#06b6d4' } },
  { id: 'e8', source: 'ai1', sourceHandle: 'pass', target: 'success', label: 'No "officiant"', style: { stroke: '#4ade80' } },
  { id: 'e9', source: 'ai1', sourceHandle: 'fail', target: 'fail', label: 'Says "officiant"', style: { stroke: '#f87171' } },
];

// ============================================
// MAIN COMPONENT
// ============================================

const FlowBuilderReactFlow = () => {
  const nodeTypes = useMemo(() => ({
    question: QuestionNode,
    condition: ConditionNode,
    document: DocumentNode,
    aiAnalysis: AIAnalysisNode,
    outcome: OutcomeNode,
    start: StartNode,
  }), []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#667eea' } }, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowInstance) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeLabels = {
        question: 'New Question',
        condition: 'New Condition',
        document: 'Request Document',
        aiAnalysis: 'AI Analysis',
        outcome: 'Outcome',
        start: 'Start',
      };

      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: { 
          label: nodeLabels[type] || 'New Node',
          success: type === 'outcome' ? true : undefined,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <PageContainer>
      <Header>
        <Title>
          🔀 Scenario Flow Builder
          <span className="badge">Option A: React Flow</span>
        </Title>
      </Header>
      
      <FlowContainer>
        <Sidebar>
          <SidebarTitle>📦 Drag Nodes</SidebarTitle>
          
          <DragNode
            gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'start')}
          >
            <NodeIcon>🚀</NodeIcon> Start
          </DragNode>
          
          <DragNode
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'question')}
          >
            <NodeIcon>❓</NodeIcon> Question
          </DragNode>
          
          <DragNode
            gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'condition')}
          >
            <NodeIcon>⚡</NodeIcon> Condition
          </DragNode>
          
          <DragNode
            gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'document')}
          >
            <NodeIcon>📄</NodeIcon> Document
          </DragNode>
          
          <DragNode
            gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'aiAnalysis')}
          >
            <NodeIcon>🤖</NodeIcon> AI Analysis
          </DragNode>
          
          <DragNode
            gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            draggable
            onDragStart={(e) => onDragStart(e, 'outcome')}
          >
            <NodeIcon>✅</NodeIcon> Outcome
          </DragNode>
        </Sidebar>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
          style={{ background: 'transparent' }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls style={{ background: 'rgba(30, 40, 60, 0.9)', borderRadius: '8px' }} />
          <MiniMap 
            pannable={true}
            zoomable={true}
            maskColor="rgba(15, 23, 42, 0.85)"
            style={{ 
              background: 'rgba(30, 41, 59, 0.95)', 
              borderRadius: '8px',
              border: '1px solid rgba(99, 102, 241, 0.3)',
            }}
            nodeColor={(node) => {
              switch (node.type) {
                case 'question': return '#667eea';
                case 'condition': return '#f59e0b';
                case 'document': return '#06b6d4';
                case 'aiAnalysis': return '#8b5cf6';
                case 'outcome': return node.data?.success ? '#10b981' : '#ef4444';
                case 'start': return '#22c55e';
                default: return '#667eea';
              }
            }}
          />
          <Background color="#334155" gap={20} />
          
          <Panel position="top-right" style={{ background: 'rgba(30, 40, 60, 0.9)', padding: '12px 16px', borderRadius: '8px', color: '#fff', fontSize: '13px' }}>
            <div style={{ marginBottom: '8px', fontWeight: '600' }}>💡 Instructions</div>
            <div style={{ opacity: 0.8, lineHeight: 1.6 }}>
              • Drag nodes from sidebar<br/>
              • Connect nodes by dragging handles<br/>
              • Click & drag to move nodes<br/>
              • Scroll to zoom
            </div>
          </Panel>
        </ReactFlow>
      </FlowContainer>
    </PageContainer>
  );
};

export default FlowBuilderReactFlow;


